import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { FieldShell, TextArea, TextField } from '@/components/ui/Field'
import { Sheet } from '@/components/ui/Sheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Profile, ProfileExperiencePayload } from '@/types/api'

import { EnhanceToolbar } from './Enhance'
import {
  FORM_GRID,
  FORM_SPAN,
  MONTHS,
  PlusIcon,
  SaveFooter,
  SectionCard,
  TrashIcon,
  formatMonth,
  useIsPhone,
  useSectionForm,
} from './shared'

const PASTE_HINT = 'Pasted. Enhance with AI to make it ATS-friendly.'

interface RoleRow {
  key: string
  company: string
  title: string
  location: string
  startMonth: string
  startYear: string
  endMonth: string
  endYear: string
  isCurrent: boolean
  description: string
  source: 'resume' | 'user'
}

/** "2020-03" -> ["03", "2020"]; a bare "2020" -> ["", "2020"]; anything else -> ["", ""]. */
function splitMonth(value: string | null): [string, string] {
  if (!value) return ['', '']
  const match = /^(\d{4})(?:-(\d{2}))?$/.exec(value.trim())
  if (!match) return ['', '']
  return [match[2] ?? '', match[1] ?? '']
}

/** The server takes "YYYY-MM", or a bare "YYYY" when only the year is known. */
function joinMonth(month: string, year: string): string | null {
  if (!year.trim()) return null
  const y = Number(year)
  if (!Number.isInteger(y) || !(y >= 1950 && y <= 2100)) return null
  return month ? `${y}-${month}` : String(y)
}

const rolesFrom = (profile: Profile): RoleRow[] =>
  (profile.experiences ?? []).map((experience) => {
    const [startMonth, startYear] = splitMonth(experience.startDate)
    const [endMonth, endYear] = splitMonth(experience.endDate)
    return {
      key: experience.id,
      company: experience.company,
      title: experience.title,
      location: experience.location ?? '',
      startMonth,
      startYear,
      endMonth,
      endYear,
      isCurrent: experience.isCurrent,
      description: experience.description ?? '',
      source: experience.source,
    }
  })

const emptyRole = (index: number): RoleRow => ({
  key: `new-${Date.now()}-${index}`,
  company: '',
  title: '',
  location: '',
  startMonth: '',
  startYear: '',
  endMonth: '',
  endYear: '',
  isCurrent: false,
  description: '',
  source: 'user',
})

/** "Mar 2020 – Present" from the draft's month and year fields. */
function roleDates(row: RoleRow): string | null {
  const start = formatMonth(joinMonth(row.startMonth, row.startYear))
  const end = row.isCurrent ? 'Present' : formatMonth(joinMonth(row.endMonth, row.endYear))
  if (!start && !end) return null
  return [start ?? '…', end ?? '…'].join(' – ')
}

export function WorkHistoryTab({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient()
  const phone = useIsPhone()
  const { form: rows, setForm: setRows, dirty, markSaved, savedAt } = useSectionForm(profile, rolesFrom)
  /** The role whose editor is open: inline on a desktop, a sheet on a phone. */
  const [editing, setEditing] = useState<string | null>(null)

  const patch = (key: string, changes: Partial<RoleRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))
  const removeRow = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key))
    if (editing === key) setEditing(null)
  }
  const addRole = () => {
    const role = emptyRole(rows.length)
    setRows((current) => [...current, role])
    setEditing(role.key)
  }

  const skillNames = (profile.skills ?? []).map((skill) => skill.name)
  /** Rows whose description is being rewritten: their textarea is locked meanwhile. */
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  /** Rows that were pasted into while empty: a nudge to enhance, until the next edit. */
  const [pasted, setPasted] = useState<Record<string, boolean>>({})
  // onPaste fires before the paste's onChange; this says the next change is that paste.
  const pasteIntoEmpty = useRef<string | null>(null)

  const clearPasted = (key: string) =>
    setPasted((current) => {
      if (!current[key]) return current
      const { [key]: _gone, ...rest } = current
      return rest
    })

  const changeDescription = (key: string, value: string) => {
    patch(key, { description: value })
    const fromPaste = pasteIntoEmpty.current === key
    pasteIntoEmpty.current = null
    if (fromPaste && value.trim()) setPasted((current) => ({ ...current, [key]: true }))
    else clearPasted(key)
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: ProfileExperiencePayload[] = []
      for (const row of rows) {
        const blank = !row.company.trim() && !row.title.trim() && !row.description.trim()
        if (blank) continue
        if (!row.company.trim() || !row.title.trim()) {
          throw new ApiError('Every role needs a company and a job title', 422, 'validation_error', null)
        }
        payload.push({
          company: row.company.trim(),
          title: row.title.trim(),
          location: row.location.trim() || null,
          startDate: joinMonth(row.startMonth, row.startYear),
          endDate: row.isCurrent ? null : joinMonth(row.endMonth, row.endYear),
          isCurrent: row.isCurrent,
          description: row.description.trim() || null,
        })
      }
      return profileApi.updateExperiences(payload)
    },
    onSuccess: () => {
      markSaved()
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profile-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['career-paths'] })
      toast.success('Saved')
    },
    onError: (error: ApiError) => toast.error('Could not save', error.message),
  })

  const editingRow = rows.find((row) => row.key === editing) ?? null
  const enhanceFor = (row: RoleRow, size: 'xs' | 'sm') => (
    <EnhanceToolbar
      kind="experience"
      size={size}
      text={row.description}
      context={{
        company: row.company.trim() || null,
        title: row.title.trim() || null,
        isCurrent: row.isCurrent,
        skills: skillNames,
      }}
      onResult={(text) => {
        patch(row.key, { description: text })
        clearPasted(row.key)
      }}
      onBusyChange={(value) => setBusy((current) => ({ ...current, [row.key]: value }))}
      hint={pasted[row.key] ? PASTE_HINT : undefined}
    />
  )

  const form = (row: RoleRow) => (
    <RoleForm
      row={row}
      busy={Boolean(busy[row.key])}
      onPatch={(changes) => patch(row.key, changes)}
      onPasteDescription={() => {
        pasteIntoEmpty.current = row.description.trim() ? null : row.key
      }}
      onChangeDescription={(value) => changeDescription(row.key, value)}
      toolbar={enhanceFor(row, 'sm')}
    />
  )

  return (
    <SectionCard
      id="profile-work-history"
      title="Work history"
      description="Help employers understand your background by adding your roles. Most recent first."
      actions={
        rows.length > 0 ? (
          <span className="text-[12.5px] text-ink-muted">
            <span className="num font-medium text-ink">{rows.length}</span> {rows.length === 1 ? 'role' : 'roles'}
          </span>
        ) : undefined
      }
      footer={<SaveFooter onSave={() => save.mutate()} saving={save.isPending} dirty={dirty} savedAt={savedAt} />}
    >
      {rows.length === 0 && (
        <p className="panel px-4 py-3 text-[14px] text-ink-muted">
          No roles yet. Add one below, or upload a resume on the Profile tab and we will read them from it.
        </p>
      )}

      <ul className="space-y-3">
        {rows.map((row, index) => {
          const open = !phone && editing === row.key
          return (
            <li key={row.key} className={cn('panel p-4 sm:p-5', open && 'border-line-strong')}>
              <RoleSummary
                row={row}
                index={index}
                editing={open}
                onEdit={() => setEditing(open ? null : row.key)}
                onRemove={() => removeRow(row.key)}
              />
              {open ? (
                <div className="mt-4 border-t border-line pt-4">{form(row)}</div>
              ) : (
                <>
                  {row.description.trim() ? (
                    <p className="mt-3 line-clamp-4 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-soft">
                      {row.description}
                    </p>
                  ) : (
                    <p className="mt-3 text-[13px] text-ink-faint">No description yet.</p>
                  )}
                  {enhanceFor(row, 'xs')}
                </>
              )}
            </li>
          )
        })}
      </ul>

      <div className="mt-3">
        <Button variant="secondary" size="sm" icon={<PlusIcon />} onClick={addRole} className="w-full sm:w-auto">
          Add role
        </Button>
      </div>

      {/* On a phone the editor is a sheet; the list stays short behind it. */}
      <Sheet
        open={phone && editingRow !== null}
        onClose={() => setEditing(null)}
        title={editingRow?.title || editingRow?.company || 'Role'}
        description="Changes are kept here until you press Save."
        footer={
          editingRow && (
            <>
              <Button
                variant="ghost"
                className="mr-auto text-danger hover:text-danger"
                icon={<TrashIcon />}
                onClick={() => removeRow(editingRow.key)}
              >
                Remove
              </Button>
              <Button onClick={() => setEditing(null)}>Done</Button>
            </>
          )
        }
      >
        {editingRow && <div className="pb-2">{form(editingRow)}</div>}
      </Sheet>
    </SectionCard>
  )
}

/** The role as it reads: title, company, dates, and the edit and remove controls. */
function RoleSummary({
  row,
  index,
  editing,
  onEdit,
  onRemove,
}: {
  row: RoleRow
  index: number
  editing: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const dates = roleDates(row)
  const subtitle = [row.company, row.location].filter((part) => part.trim()).join(' · ')
  return (
    <header className="flex items-start gap-3">
      <span
        aria-hidden
        className="num grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-muted text-[12px] font-semibold text-ink-muted"
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-[15px] font-semibold leading-snug text-ink">
            {row.title.trim() || <span className="font-normal text-ink-faint">Untitled role</span>}
          </h3>
          {row.source === 'resume' && (
            <StatusPill size="sm" tone="neutral">
              From your resume
            </StatusPill>
          )}
          {row.isCurrent && (
            <StatusPill size="sm" tone="brand">
              Current
            </StatusPill>
          )}
        </div>
        {subtitle && <p className="mt-0.5 text-[13.5px] text-ink-soft">{subtitle}</p>}
        {dates && <p className="num mt-0.5 text-[12.5px] text-ink-muted">{dates}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="xs" variant={editing ? 'primary' : 'secondary'} onClick={onEdit} aria-expanded={editing}>
          {editing ? 'Done' : 'Edit'}
        </Button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${row.title || 'role'}`}
          title="Remove"
          className="hidden h-8 w-8 place-items-center rounded-[8px] text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger md:grid"
        >
          <TrashIcon />
        </button>
      </div>
    </header>
  )
}

/** Every field of one role. The description's Enhance toolbar comes from the tab. */
function RoleForm({
  row,
  busy,
  onPatch,
  onPasteDescription,
  onChangeDescription,
  toolbar,
}: {
  row: RoleRow
  busy: boolean
  onPatch: (changes: Partial<RoleRow>) => void
  onPasteDescription: () => void
  onChangeDescription: (value: string) => void
  toolbar: React.ReactNode
}) {
  return (
    <div className={FORM_GRID}>
      <TextField
        label="Company name"
        required
        appearance="box"
        value={row.company}
        onChange={(event) => onPatch({ company: event.target.value })}
      />
      <TextField
        label="Job title"
        required
        appearance="box"
        value={row.title}
        onChange={(event) => onPatch({ title: event.target.value })}
      />
      <TextField
        label="Location"
        appearance="box"
        placeholder="Bengaluru, India · Remote"
        value={row.location}
        onChange={(event) => onPatch({ location: event.target.value })}
        className={FORM_SPAN}
      />
      <MonthYear
        label="Start"
        month={row.startMonth}
        year={row.startYear}
        onChange={(month, year) => onPatch({ startMonth: month, startYear: year })}
      />
      {row.isCurrent ? (
        <FieldShell label="End">
          <label className="flex h-11 cursor-pointer items-center gap-2.5 rounded-field border border-line px-3.5 text-[14px] text-ink">
            <input
              type="checkbox"
              checked={row.isCurrent}
              onChange={(event) => onPatch({ isCurrent: event.target.checked })}
              className="h-4 w-4 rounded border-line-strong accent-ink"
            />
            Currently work here
          </label>
        </FieldShell>
      ) : (
        <div className="space-y-2">
          <MonthYear
            label="End"
            month={row.endMonth}
            year={row.endYear}
            onChange={(month, year) => onPatch({ endMonth: month, endYear: year })}
          />
          <label className="flex min-h-[32px] cursor-pointer items-center gap-2.5 text-[13.5px] text-ink-soft">
            <input
              type="checkbox"
              checked={row.isCurrent}
              onChange={(event) => onPatch({ isCurrent: event.target.checked })}
              className="h-4 w-4 rounded border-line-strong accent-ink"
            />
            Currently work here
          </label>
        </div>
      )}
      <div className={FORM_SPAN}>
        <TextArea
          label="Description"
          appearance="box"
          rows={5}
          placeholder={'One bullet per line:\nLed migration of the billing service to Kubernetes\nCut p95 latency by 40%'}
          value={row.description}
          disabled={busy}
          onPaste={onPasteDescription}
          onChange={(event) => onChangeDescription(event.target.value)}
        />
        {toolbar}
      </div>
    </div>
  )
}

function MonthYear({
  label,
  month,
  year,
  onChange,
}: {
  label: string
  month: string
  year: string
  onChange: (month: string, year: string) => void
}) {
  return (
    <FieldShell label={label}>
      <div className="grid grid-cols-[minmax(0,1fr)_104px] gap-2">
        <span className="relative">
          <select
            aria-label={`${label} month`}
            value={month}
            onChange={(event) => onChange(event.target.value, year)}
            className={cn('field-box h-11 appearance-none pr-8', !month && 'text-ink-faint')}
          >
            <option value="">Month</option>
            {MONTHS.map((name, index) => (
              <option key={name} value={String(index + 1).padStart(2, '0')}>
                {name}
              </option>
            ))}
          </select>
          <svg
            className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>
        <input
          aria-label={`${label} year`}
          type="number"
          inputMode="numeric"
          min={1950}
          max={2100}
          placeholder="Year"
          value={year}
          onChange={(event) => onChange(month, event.target.value)}
          className="field-box num h-11"
        />
      </div>
    </FieldShell>
  )
}
